import React, { useState, useRef, useEffect } from 'react';

import Button from '../../../components/ui/Button';
import EditorToolbar from './EditorToolbar';

const ContentEditor = ({ content, onChange }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [activeFormats, setActiveFormats] = useState([]);
  const textareaRef = useRef(null);

  const handleFormat = (formatType) => {
    const textarea = textareaRef?.current;
    if (!textarea) return;

    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const selectedText = content?.substring(start, end);
    let newText = content;
    let newCursorPos = end;

    const formatMap = {
      bold: { prefix: '**', suffix: '**' },
      italic: { prefix: '_', suffix: '_' },
      underline: { prefix: '<u>', suffix: '</u>' },
      strikethrough: { prefix: '~~', suffix: '~~' },
      h1: { prefix: '# ', suffix: '' },
      h2: { prefix: '## ', suffix: '' },
      h3: { prefix: '### ', suffix: '' },
      bulletList: { prefix: '- ', suffix: '' },
      numberedList: { prefix: '1. ', suffix: '' },
      code: { prefix: '```\n', suffix: '\n```' },
      quote: { prefix: '> ', suffix: '' },
      link: { prefix: '[', suffix: '](url)' }
    };

    if (formatMap?.[formatType]) {
      const { prefix, suffix } = formatMap?.[formatType];
      newText = content?.substring(0, start) + prefix + selectedText + suffix + content?.substring(end);
      newCursorPos = start + prefix?.length + selectedText?.length + suffix?.length;
    }

    onChange(newText);
    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const renderPreview = () => {
    let html = content;
    
    html = html?.replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mb-4">$1</h1>');
    html = html?.replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mb-3">$1</h2>');
    html = html?.replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mb-2">$1</h3>');
    
    html = html?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html?.replace(/_(.*?)_/g, '<em>$1</em>');
    html = html?.replace(/~~(.*?)~~/g, '<del>$1</del>');
    
    html = html?.replace(/^- (.*$)/gim, '<li class="ml-4">$1</li>');
    html = html?.replace(/^(\d+)\. (.*$)/gim, '<li class="ml-4">$2</li>');
    
    html = html?.replace(/```([\s\S]*?)```/g, '<pre class="bg-muted p-4 rounded-lg overflow-x-auto"><code>$1</code></pre>');
    
    html = html?.replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-primary pl-4 italic">$1</blockquote>');
    
    html = html?.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline">$1</a>');
    
    html = html?.replace(/\n/g, '<br />');

    return html;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-semibold text-foreground">Content</h3>
        <div className="flex items-center gap-2">
          <Button
            variant={showPreview ? 'ghost' : 'secondary'}
            size="sm"
            iconName="FileEdit"
            iconPosition="left"
            onClick={() => setShowPreview(false)}
          >
            <span className="hidden md:inline">Edit</span>
          </Button>
          <Button
            variant={showPreview ? 'secondary' : 'ghost'}
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => setShowPreview(true)}
          >
            <span className="hidden md:inline">Preview</span>
          </Button>
        </div>
      </div>
      {!showPreview && (
        <EditorToolbar onFormat={handleFormat} activeFormats={activeFormats} />
      )}
      <div className="relative">
        {showPreview ? (
          <div
            className="min-h-[400px] md:min-h-[500px] p-4 md:p-6 bg-background border border-input rounded-lg prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: renderPreview() }}
          />
        ) : (
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => onChange(e?.target?.value)}
            placeholder="Write your blog post content here using Markdown formatting..."
            className="w-full min-h-[400px] md:min-h-[500px] p-4 md:p-6 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-y font-mono text-sm md:text-base"
            style={{ lineHeight: '1.6' }}
          />
        )}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
        <span>{content?.length} characters</span>
        <span>{content?.split(/\s+/)?.filter(Boolean)?.length} words</span>
      </div>
    </div>
  );
};

export default ContentEditor;